import { format, subDays, subMonths, subWeeks, subYears } from 'date-fns'
import { useRef } from 'react'
import { DeviceStatusDataType } from '../context/DeviceStatusContext'
import { Alarm } from '../features/alarms/api/types'
import { IDevice, Location } from '../features/reports/api/types'
import {
  KILOWATT_HOUR,
  KILOWATT,
  ENERGY_PLANT_LOGGER,
  ENERGY_GENERATOR,
  REPORT_TYPES,
} from './consts'
import dayjs from 'dayjs'

export const mutatePriceUnit = (priceUnit: string) => {
  if (priceUnit === KILOWATT) {
    return KILOWATT_HOUR
  }
}

export const splitAndUppercase = (string: string, delimeter: string) =>
  string
    .split(delimeter)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

export const addCommasToNumber = (num: number | string) => {
  if (typeof num === 'string') {
    return num
  }

  if (!num) {
    return Number(0).toLocaleString()
  }

  return parseFloat(num.toFixed(2)).toLocaleString()
}

export const getLocationIndex = (data: any, locationId: string | null) => {
  return (
    data &&
    data.length > 0 &&
    data.findIndex((item: any) => item.id.toString() === locationId) + 1
  )
}

export const calculatePercentage = (
  part: number,
  whole: number,
  roundingIndex: number = 0,
): number => {
  if (whole === 0 || part === 0) {
    return 0
  }
  const result = Number(((part / whole) * 100).toFixed(roundingIndex))

  return result
}

const priceUnitMap = {
  kW: KILOWATT_HOUR,
  MW: 'mWh',
}

export const getPriceUnitMap = (priceUnit?: keyof typeof priceUnitMap) =>
  priceUnitMap[priceUnit || KILOWATT]

const DEVICE_STATUS = {
  null: 'N/A',
  undefined: 'N/A',
  0: 'Idle',
  1: 'On-Grid',
  2: 'On-Grid: self derating',
  3: 'On-Grid: Power limit',
  4: 'Planned outage',
  5: 'Power limit outage',
  6: 'Fault outage',
  7: 'Communication interrupt',
}

export type DeviceStatusKeys = keyof typeof DEVICE_STATUS

export const getDeviceStatus = (status: DeviceStatusKeys): string => DEVICE_STATUS[status]

export const getDeviceCurrentDataFieldsByCategory = (category) => {
  switch (category) {
    case ENERGY_PLANT_LOGGER:
      return ['generated_power', 'current', 'input_power', 'dc_current']
    case ENERGY_GENERATOR:
      return []
    default:
      return ['consumption']
  }
}

export const getSubdomain = () => window.location.hostname.split('.')[0]

export const METHODIA_SUBDOMAIN = 'methodia'

export const downloadFile = (href, filename) => {
  const link = document.createElement('a')
  link.href = href
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

export function formatDate(date) {
  const options = { day: '2-digit', month: 'short', year: 'numeric' }
  return date?.toLocaleDateString('en-GB', options).replace(',', '')
}

export const deviceGroupSumByProperty = (devices: IDevice[], property: string) => {
  return devices.reduce((total, item) => total + (item[property] || 0), 0)
}

export const formatDateString = (date) => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export const getReportNameById = (id: number): string | undefined => {
  return REPORT_TYPES.find((report) => report.id === id)?.name
}

export const setTemplateCount = (
  setBookmarkTemplateCount: (value: number) => void,
  setRecurringTemplateCount: (value: number) => void,
  templateType: string,
  templateCount,
) => {
  switch (templateType) {
    case 'bookmark':
      setBookmarkTemplateCount(templateCount)
      break
    case 'recurring':
      setRecurringTemplateCount(templateCount)
      break
    default:
      break
  }
}

export const blobToBase64 = (blob: Blob): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => {
      if (typeof reader.result === 'string') {
        resolve(reader.result.split(',')[1] || '')
      } else {
        reject(new Error('FileReader result is not a string'))
      }
    }
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })

export const generateTooltip = (templateCount, templateLimit, reportType) => {
  if (templateCount >= templateLimit) {
    return `You have reached the limit of saved samples (${templateLimit}). To save a new one, please remove an old sample.`
  } else {
    return `${reportType} Report`
  }
}

export const scrollBarStyle = {
  '&::-webkit-scrollbar': {
    display: 'none',
  },
  '&::-webkit-scrollbar-track': {
    background: 'gray.200',
    borderRadius: '0 0.25em 0.25em 0',
  },
  '&::-webkit-scrollbar-thumb': {
    backgroundColor: 'blue.500',
    borderRadius: '0.25em',
    border: '2px solid transparent',
    backgroundClip: 'padding-box',
  },
}

export const calculateStepSize = (range) => {
  const roughStep = range / 10
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)))
  const niceStepSize = Math.ceil(roughStep / magnitude) * magnitude

  return niceStepSize
}

export function getLocationsPercentages(
  location: Location,
  current: string,
  previous: string,
) {
  const currentValue = location[current]
  const previousValue = location[previous]

  if (currentValue > 0 && previousValue > 0) {
    const percentageChange = ((previousValue - currentValue) / previousValue) * 100
    return Number(percentageChange.toFixed(2))
  }
  return 0
}

export function getDevices(alarm: Alarm) {
  if (alarm.device) {
    return alarm?.devices.filter((device) => device.id === alarm.device)
  }
  return alarm?.devices
}

export function getAlarmSeverity(alarm: Alarm, activeIndex) {
  const type =
    activeIndex < 2 && Array.isArray(alarm?.multistate_alarm?.multistate_fields)
      ? alarm.multistate_alarm.multistate_fields.find(
          (x) => Number(x.value) === Number(alarm?.value),
        )
      : alarm

  return type?.severity
}

export const formatAlarmDate = (date, placeholder = '-') => {
  try {
    return date ? format(date, 'yyyy-MM-dd HH:mm:ss') : placeholder
  } catch (error) {
    console.error('Error formatting date:', error)
    return placeholder
  }
}

export const getFilteredFields = (activeTab, multistateValue, fields) => {
  if (activeTab === 0 || activeTab === 1) {
    return fields.filter((x) => x.value === multistateValue)
  }
  return fields
}

export const debounce = (func, delay) => {
  let timeoutId
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      func(...args)
    }, delay)
  }
}

export function useDebouncedCallback<T extends (...args: any[]) => void>(
  callback: T,
  delay: number,
): T {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  return ((...args: any[]) => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(() => {
      callback(...args)
    }, delay)
  }) as T
}

export const findDeviceOutput = (
  deviceId: number,
  parameter: string,
  deviceStatusData: DeviceStatusDataType[],
) => {
  if (Array.isArray(deviceStatusData)) {
    const deviceStatus = deviceStatusData.find(
      (x) => x.id === deviceId && x.parameter === parameter,
    )
    return deviceStatus?.output ? deviceStatus.output : false
  }
  return true
}
export const findDeviceBrightness = (
  deviceId: number,
  parameter: string,
  deviceStatusData: DeviceStatusDataType[],
) => {
  if (Array.isArray(deviceStatusData)) {
    const deviceStatus = deviceStatusData.find(
      (x) => x.id === deviceId && x.parameter === parameter,
    )
    return deviceStatus?.brightness ? deviceStatus.brightness : undefined
  }
  return undefined
}

export const isTemperatureParam = (param: string) => param === 'temperature'

export function getLocationsPercentage(currentValue: number, previousValue: number) {
  if (currentValue > 0 && previousValue > 0) {
    const percentageChange = ((previousValue - currentValue) / previousValue) * 100
    return Number(percentageChange.toFixed(2))
  }
  return 0
}

export const parameterTitleMap: Record<string, string> = {
  totalgeneratedenergy: 'Produced Energy',
  totalconsumedenergy: 'Consumed Energy',
  temperature: 'Temperature',
  reactiveenergy: 'Reactive Energy',
}

export const iconParameterMap: Record<string, any> = {
  totalgeneratedenergy: 'Produced Energy',
  totalconsumedenergy: 'Consumed Energy',
  temperature: 'Temperature',
}

export const parameterColumnMap: Record<string, string> = {
  totalgeneratedenergy: 'Production',
  totalconsumedenergy: 'Consumation',
  temperature: 'Temperature',
}

export const costIncomeMap: Record<string, string> = {
  totalgeneratedenergy: 'Income',
  totalconsumedenergy: 'Cost',
}

export const measurementTypeMap: Record<string, string> = {
  totalgeneratedenergy: 'kWh',
  totalconsumedenergy: 'kWh',
  temperature: '°C',
  reactiveenergy: 'kVArh',
}
export const locationOverviewParameters = [
  'totalgeneratedenergy',
  'totalconsumedenergy',
  'temperature',
]

export const currentMeasurementTypeMap: Record<string, string> = {
  totalgeneratedenergy: 'kW',
  totalconsumedenergy: 'kW',
  temperature: '°C',
  reactiveenergy: 'kVAr',
}
export const currentSummaryTitle: Record<string, string> = {
  totalgeneratedenergy: 'Produced Power',
  totalconsumedenergy: 'Consumed Power',
  temperature: 'Temperature',
  reactiveenergy: 'Reactive Power',
}

export function getReadableValue(mapperFnc: any, value: string): string {
  return mapperFnc[value] || value
}

export function getDateRange(periodName) {
  const now = new Date()
  let start

  switch (periodName) {
    case '1 day':
      start = subDays(now, 1)
      break
    case '1 week':
      start = subWeeks(now, 1)
      break
    case '1 month':
      start = subMonths(now, 1)
      break
    case '1 year':
      start = subYears(now, 1)
      break
    default:
      start = subDays(now, 1)
  }

  return {
    from: format(start, 'yyyy-MM-dd'),
    to: format(now, 'yyyy-MM-dd'),
  }
}

export const getChartColor = (parameterTitle: string): string => {
  switch (parameterTitle) {
    case 'totalconsumedenergy':
      return '#A4D8FD'
    case 'totalgeneratedenergy':
      return '#ADEED0'
    case 'temperature':
      return '#CECFD0'
    default: {
      const hue = Math.floor(Math.random() * 360)
      return `hsl(${hue}, 70%, 80%)`
    }
  }
}

export const getShadeFromPercent = (hex: string, percent: number): string => {
  const hexToHSL = (H: string) => {
    let r = 0,
      g = 0,
      b = 0
    if (H.length === 4) {
      r = parseInt(H[1] + H[1], 16)
      g = parseInt(H[2] + H[2], 16)
      b = parseInt(H[3] + H[3], 16)
    } else {
      r = parseInt(H[1] + H[2], 16)
      g = parseInt(H[3] + H[4], 16)
      b = parseInt(H[5] + H[6], 16)
    }

    r /= 255
    g /= 255
    b /= 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    let h = 0,
      s = 0,
      l = (max + min) / 2

    if (max !== min) {
      const d = max - min
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
      switch (max) {
        case r:
          h = (g - b) / d + (g < b ? 6 : 0)
          break
        case g:
          h = (b - r) / d + 2
          break
        case b:
          h = (r - g) / d + 4
          break
      }
      h /= 6
    }

    return { h, s, l }
  }

  const hslToCSS = (h: number, s: number, l: number) =>
    `hsl(${Math.round(h * 360)}, ${Math.round(s * 100)}%, ${Math.round(l * 100)}%)`

  const { h, s } = hexToHSL(hex)
  const minL = 0.3
  const maxL = 0.9
  const l = maxL - (percent / 100) * (maxL - minL)

  return hslToCSS(h, s, l)
}

export const getPeriodLabelFromDates = (from: string | null, to: string | null): any => {
  if (!from || !to) return 'period'

  const fromDate = dayjs(from)
  const toDate = dayjs(to)
  const diff = toDate.diff(fromDate, 'day')

  if (diff <= 1) return 'day'
  if (diff <= 7) return 'week'
  if (diff <= 31) return 'month'
  if (diff <= 370) return 'year'
  return 'custom period'
}

export interface Parameter {
  parameter_title: string
  measurement_unit: string
  value: number
  previous_value: number
  period: { bucket: string; value: number }[]
  previous_period: { bucket: string; value: number }[]
}

type ChartEntry =
  | { type: 'single'; parameter: Parameter }
  | { type: 'grouped'; title: string; parameters: Parameter[] }

const PHASE_SUFFIXES = ['A', 'B', 'C']

function getBaseTitle(title: string): string | null {
  for (const suffix of PHASE_SUFFIXES) {
    if (title.endsWith(suffix)) {
      return title.slice(0, -suffix.length)
    }
  }
  return null
}

export function organizeParameters(parameters: Parameter[]): ChartEntry[] {
  const remaining = [...parameters]
  const result: ChartEntry[] = []

  const titleMap = new Map(parameters.map((p) => [p.parameter_title, p]))
  const grouped = new Set<string>()

  for (const param of parameters) {
    const baseTitle = getBaseTitle(param.parameter_title)
    if (!baseTitle || grouped.has(baseTitle)) continue

    const expectedPhaseTitles = PHASE_SUFFIXES.map((suffix) => `${baseTitle}${suffix}`)
    const extendedTitles = [baseTitle, ...expectedPhaseTitles]

    const groupParams = extendedTitles
      .map((title) => titleMap.get(title))
      .filter(Boolean) as Parameter[]

    if (groupParams.length === extendedTitles.length) {
      result.push({ type: 'grouped', title: baseTitle, parameters: groupParams })
      grouped.add(baseTitle)

      for (const p of groupParams) {
        const index = remaining.findIndex((r) => r.parameter_title === p.parameter_title)
        if (index !== -1) remaining.splice(index, 1)
      }
    }
  }

  for (const param of remaining) {
    result.push({ type: 'single', parameter: param })
  }

  return result
}

export function uniqueTitlesReducer(titles: string[]): string[] {
  if (!Array.isArray(titles)) {
    return [];
  }
  return titles.reduce<string[]>((unique, title) => {
    if (!unique.includes(title)) {
      unique.push(title);
    }
    return unique;
  }, []);
}